import PageBackTitle from "../Component/PageBackTitle";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { FiDownload, FiRefreshCw } from "react-icons/fi";
import API_URL from "../Config";
import "./miIndex.css";
import {
  MI_STATUS_OPTIONS,
  fetchMiLocations,
  formatMiDate,
  formatMiNumber,
  parseMiApiError,
} from "./miIndexUtils";

const INITIAL_FILTERS = {
  start_date: "",
  end_date: "",
  location_id: "",
  status: "",
};

function MIReports({ isSidebarExpanded }) {
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [reportData, setReportData] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const loadLocations = useCallback(async () => {
    try {
      const response = await fetchMiLocations({ per_page: 1000, page: 1 });
      setLocations(response.locations || []);
    } catch (error) {
      showSnackbar(parseMiApiError(error, "Failed to load locations"), "error");
    }
  }, [showSnackbar]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const selectedLocation = useMemo(
    () =>
      locations.find((location) => location.location_id === filters.location_id) ||
      null,
    [filters.location_id, locations],
  );

  const buildFilterParams = () => {
    const params = {};
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (filters.location_id) params.location_id = filters.location_id;
    if (filters.status) params.status = filters.status;
    return params;
  };

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/mi/reports/summary`, {
        headers: {
          Authorization: localStorage.getItem("authKey") || "",
        },
        params: buildFilterParams(),
      });

      setReportData(response.data || null);
      showSnackbar("MI summary report generated successfully");
    } catch (error) {
      showSnackbar(
        parseMiApiError(error, "Failed to generate MI summary report"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API_URL}/api/mi/reports/export`, {
        headers: {
          Authorization: localStorage.getItem("authKey") || "",
        },
        params: {
          format,
          ...buildFilterParams(),
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `MI_Report_${Date.now()}.${
        format === "excel" ? "xlsx" : "csv"
      }`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSnackbar(
        `MI report exported successfully as ${format === "excel" ? "Excel" : "CSV"}`,
      );
    } catch (error) {
      showSnackbar(parseMiApiError(error, "Failed to export report"), "error");
    }
  };

  const handleRefresh = () => {
    setFilters(INITIAL_FILTERS);
    setReportData(null);
  };

  const overall = reportData?.overall_statistics || {};
  const locationPerformance = reportData?.location_performance || [];

  return (
    <>
      <div
        className={`data-list-content-container page-content mi-page ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content mi-page-layout">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>MI Index Reports</PageBackTitle>
              <div className="data-list-heading-content-right-side mi-standard-header__actions">
                <Link to="/mi-evaluations">
                  <button className="mi-btn mi-btn--secondary" type="button">
                    Open Evaluations
                  </button>
                </Link>
                <button
                  className="mi-btn mi-btn--secondary"
                  type="button"
                  onClick={handleRefresh}
                >
                  <FiRefreshCw /> Reset
                </button>
              </div>
            </div>

            <div className="mi-standard-header__subtitle">
              Generate location-wise MI performance summaries and export them in
              CSV or Excel format.
            </div>
          </div>
          <div className="header-separator"></div>

          <div className="data-view-body">
          <div className="mi-shell">

          <div className="mi-card mi-toolbar">
            <div className="mi-toolbar__grid">
              <div className="mi-field">
                <label>Start Date</label>
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(event) =>
                    setFilters((prevState) => ({
                      ...prevState,
                      start_date: event.target.value,
                    }))
                  }
                />
              </div>

              <div className="mi-field">
                <label>End Date</label>
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(event) =>
                    setFilters((prevState) => ({
                      ...prevState,
                      end_date: event.target.value,
                    }))
                  }
                />
              </div>

              <div className="mi-field">
                <label>Location</label>
                <select
                  value={filters.location_id}
                  onChange={(event) =>
                    setFilters((prevState) => ({
                      ...prevState,
                      location_id: event.target.value,
                    }))
                  }
                >
                  <option value="">All locations</option>
                  {locations.map((location) => (
                    <option key={location.location_id} value={location.location_id}>
                      {location.location_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mi-field">
                <label>Status</label>
                <select
                  value={filters.status}
                  onChange={(event) =>
                    setFilters((prevState) => ({
                      ...prevState,
                      status: event.target.value,
                    }))
                  }
                >
                  <option value="">All statuses</option>
                  {MI_STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mi-divider"></div>

            <div className="mi-footer-actions">
              <div className="mi-note">
                Use filters to generate a summary report. Export becomes available
                once the summary is loaded.
              </div>
              <div className="mi-inline-actions">
                <button
                  className="mi-btn mi-btn--primary"
                  type="button"
                  onClick={handleGenerateReport}
                  disabled={loading}
                >
                  {loading ? "Generating..." : "Generate Report"}
                </button>
                <button
                  className="mi-btn mi-btn--secondary"
                  type="button"
                  onClick={() => handleExport("csv")}
                  disabled={!reportData}
                >
                  <FiDownload /> Export CSV
                </button>
                <button
                  className="mi-btn mi-btn--secondary"
                  type="button"
                  onClick={() => handleExport("excel")}
                  disabled={!reportData}
                >
                  <FiDownload /> Export Excel
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loader-container">
              <div className="loader"></div>
              <div>Generating MI report...</div>
            </div>
          ) : reportData ? (
            <>
              <div className="mi-stats" style={{padding:"0px"}}>
                <div className="mi-card mi-stat">
                  <div className="mi-stat__label">Total Evaluations</div>
                  <div className="mi-stat__value">
                    {overall.total_evaluations || 0}
                  </div>
                  <div className="mi-stat__meta">Matching the current filters</div>
                </div>
                <div className="mi-card mi-stat">
                  <div className="mi-stat__label">Locations Evaluated</div>
                  <div className="mi-stat__value">
                    {overall.locations_evaluated || 0}
                  </div>
                  <div className="mi-stat__meta">
                    {selectedLocation
                      ? `Filtered to ${selectedLocation.location_name}`
                      : "Across all selected locations"}
                  </div>
                </div>
                <div className="mi-card mi-stat">
                  <div className="mi-stat__label">Average MI Score</div>
                  <div className="mi-stat__value">
                    {formatMiNumber(overall.avg_mi_score || 0)}
                  </div>
                  <div className="mi-stat__meta">
                    Range: {formatMiNumber(overall.min_mi_score || 0)} to{" "}
                    {formatMiNumber(overall.max_mi_score || 0)}
                  </div>
                </div>
                <div className="mi-card mi-stat">
                  <div className="mi-stat__label">Approved / Draft</div>
                  <div className="mi-stat__value">
                    {(overall.approved_count || 0) + " / " + (overall.draft_count || 0)}
                  </div>
                  <div className="mi-stat__meta">
                    Approved: {overall.approved_count || 0}, Draft:{" "}
                    {overall.draft_count || 0}
                  </div>
                </div>
              </div>

              {/* <div className="mi-section mi-card">
                <div className="mi-section__header">
                  <div>
                    <h2 className="mi-section__title">Applied Filters</h2>
                    <p className="mi-section__subtitle">
                      Current summary scope used for the report and export actions.
                    </p>
                  </div>
                </div>

                <div className="mi-readonly-grid">
                  <div className="mi-readonly">
                    <div className="mi-readonly__label">Date Range</div>
                    <div className="mi-readonly__value">
                      {filters.start_date ? formatMiDate(filters.start_date) : "Any"} to{" "}
                      {filters.end_date ? formatMiDate(filters.end_date) : "Any"}
                    </div>
                  </div>
                  <div className="mi-readonly">
                    <div className="mi-readonly__label">Location</div>
                    <div className="mi-readonly__value">
                      {selectedLocation?.location_name || "All locations"}
                    </div>
                  </div>
                  <div className="mi-readonly">
                    <div className="mi-readonly__label">Status</div>
                    <div className="mi-readonly__value">
                      {filters.status || "All statuses"}
                    </div>
                  </div>
                  <div className="mi-readonly">
                    <div className="mi-readonly__label">Export Availability</div>
                    <div className="mi-readonly__value">
                      CSV and Excel exports are ready for this summary.
                    </div>
                  </div>
                </div>
              </div> */}

              <div className="mi-section mi-card" style={{margin:"0px"}}>
                <div className="mi-section__header">
                  <div>
                    <h2 className="mi-section__title">Location Performance</h2>
                    <p className="mi-section__subtitle">
                      Average MI score by location based on the current report
                      filters.
                    </p>
                  </div>
                </div>

                {locationPerformance.length > 0 ? (
                  <div className="mi-table-wrap">
                    <table className="mi-table">
                      <thead>
                        <tr>
                          <th>Location</th>
                          <th>Type</th>
                          <th>Region</th>
                          <th>Evaluations</th>
                          <th>Average MI Score</th>
                          <th>Last Evaluation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {locationPerformance.map((location) => (
                          <tr key={location.location_id}>
                            <td>{location.location_name || "NA"}</td>
                            <td>{location.location_type || "NA"}</td>
                            <td>{location.region || "NA"}</td>
                            <td>{location.evaluation_count || 0}</td>
                            <td>{formatMiNumber(location.avg_mi_score || 0)}</td>
                            <td>{formatMiDate(location.last_evaluation_date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="mi-empty">
                    No location-wise results were returned for the selected filters.
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="mi-section mi-card" style={{margin:"0px"}}>
              <div className="mi-empty">
                Generate a report to see MI summary statistics and location-wise
                performance.
              </div>
            </div>
          )}
          </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default MIReports;
